/* Copyright (c) 2017 Backpack Health LLC */

/* global window, document, cordova */

/*
Usage

All of the ScrollAware components need to be children of ScrollAwareContext. They do not have to direct children and can be heavily nested.
ScrollAwareScroller should contain the area that actually scrolls
ScrollAwareItem is an item that scrolls, but makes use of some scroll value
  hasDropShadow: will have a drop shadow whose opacity reflects the percentage scrolled
  isSticky: will become fixed when it nears the top
  title: if scrolls up and is hiddem, this will become the title in the header
  displayTitle: available title if any
ScrollAwareFade adds an item that has a fade gradient of the active theme color.

*/

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import * as _ from 'lodash';

import { isMobileApplication, updateStatusBarColor } from '../../utilities/MobileUtil';
import ScrollAwareDropShadow from './ScrollAwareDropShadow';
import AppNavLeftRightNavigation from './appNav/appNavBits/AppNavLeftRightNavigation';
import AppNavHeaderTitle from './appNav/appNavBits/AppNavHeaderTitle';
import { selectors as groupSelectors } from '../redux/ducks/group';
import { selectors as colorThemeSelector } from '../redux/ducks/lists/colorTheme';
import AppNavStepProgress from './appNav/appNavBits/AppNavStepProgress';

import AppHeader from '../applicationFrame/AppHeader';
import UI_CONSTANTS from '../../constants/uiConstants';

function easeInOutQuad(currentTime, startValue, valueDelta, duration) {
  currentTime /= duration / 2;
  if (currentTime < 1) {
    return ((valueDelta / 2) * currentTime * currentTime) + startValue;
  }
  currentTime--;
  return -((valueDelta / 2) * ((currentTime * (currentTime - 2)) - 1)) + startValue;
}

const ScrollContext = React.createContext({
  scrollPosY: 0,
  scrollPercent: 0,
  mobileInnerHeight: 0,
  hidePosition: 56,
  stickyPosition: 56,
  usesWindowScrolling: false,
});

class ScrollAware extends React.Component {
  constructor(props) {
    super(props);

    this.handleScroll = this.handleScroll.bind(this);
    this.handleScrollIntoView = this.handleScrollIntoView.bind(this);

    this.refElement = React.createRef();

    this.state = {
      isHeaderLoaded: !props.header,
      height: window.innerHeight,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleScroll);

    if (isMobileApplication()) {
      window.addEventListener('native.keyboardshow', this.handleScrollIntoView);
      window.addEventListener('native.keyboardhide', this.handleScrollIntoView);
      this.refElement.current.addEventListener('touchmove', this.handleScroll);
      this.refElement.current.addEventListener('touchstart', this.handleScroll);
      this.refElement.current.addEventListener('touchend', this.handleScroll);
    } else {
      this.refElement.current.addEventListener('scroll', this.handleScroll);
    }

    const { usesWindowScrolling, initialScroll } = this.props;
    if (usesWindowScrolling) {
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = initialScroll;
      } else {
        document.body.scrollTop = initialScroll;
      }
    } else {
      this.setState({
        height: this.state.height - this.refElement.current.getBoundingClientRect().top,
      });
      this.refElement.current.scrollTop = initialScroll;
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleScroll);
    if (isMobileApplication()) {
      window.removeEventListener('native.keyboardshow', this.handleScrollIntoView);
      window.removeEventListener('native.keyboardhide', this.handleScrollIntoView);
      this.refElement.current.removeEventListener('touchmove', this.handleScroll);
      this.refElement.current.removeEventListener('touchstart', this.handleScroll);
      this.refElement.current.removeEventListener('touchend', this.handleScroll);
    } else {
      this.refElement.current.removeEventListener('scroll', this.handleScroll);
    }
  }

  animateScrollTo = (destinationPos, duration = 600) => {
    const element = this.refElement.current;
    const startPos = element.scrollTop;
    const deltaPos = destinationPos - startPos;
    const startTime = +new Date();

    const animateScroll = () => {
      const currentTime = +new Date();
      const diffTime = currentTime - startTime;
      element.scrollTop = Number(easeInOutQuad(diffTime, startPos, deltaPos, duration));

      if (diffTime < duration) {
        window.requestAnimationFrame(animateScroll);
      } else {
        element.scrollTop = destinationPos;
      }
    };

    if (duration <= 0) {
      element.scrollTop = destinationPos;
    } else {
      animateScroll();
    }
  }

  scrollPos = () => {
    const element = this.refElement.current;
    return element.scrollTop;
  }

  scrollBy = (destinationPos) => {
    const element = this.refElement.current;
    element.scrollBy(0, destinationPos);
  }

  handleScrollIntoView() {
    if (isMobileApplication()) {
      document.activeElement.scrollIntoView(false);
    }

    this.handleScroll();
  }

  handleScroll() {
    this.props.onScrollContext(this.refElement.current.scrollTop, this.refElement.current.scrollHeight);
    this.props.onScroll(this.refElement.current.scrollTop);
  }

  keyboardHeight;

  render() {
    const scrollBehavior = this.props.usesWindowScrolling ? null : 'auto';
    const overflowY = this.props.disableScrolling ? 'hidden' : scrollBehavior;

    return (
      <div style={this.props.outerStyle}>
        {
          this.props.header && (
            <ScrollAwareHeader scrollPercent={this.props.scrollPercent} onGotHeight={() => { this.setState({ isHeaderLoaded: true }); }}>
              {this.props.header}
            </ScrollAwareHeader>
          )
        }

        <div
          ref={this.refElement}
          className={classnames('cmp-scrollaware', this.props.className)}
          style={{
            ...this.props.innerStyle,
            marginBottom: this.props.marginBottom,
            backgroundColor: this.props.cssColor,
            overflowY,
            width: this.props.usesWindowScrolling ? null : '100%',
            height: this.props.usesWindowScrolling ? null : this.props.innerStyle.height || (this.state.height - this.props.heightOffset),
          }}
        >
          {this.state.isHeaderLoaded ? this.props.children : null}
        </div>

      </div>
    );
  }
}
ScrollAware.propTypes = {
  children: PropTypes.any,
  mobileInnerHeight: PropTypes.number,
  scrollPercent: PropTypes.number,
  cssColor: PropTypes.string,
  header: PropTypes.any,
  heightOffset: PropTypes.number,
  customHeight: PropTypes.any,
  marginBottom: PropTypes.number,
  initialScroll: PropTypes.number,
  onScroll: PropTypes.func,
  onScrollContext: PropTypes.func,
  scrollEventId: PropTypes.string,
  usesWindowScrolling: PropTypes.bool,
  outerStyle: PropTypes.object,
  innerStyle: PropTypes.object,
  disableScrolling: PropTypes.bool,
  className: PropTypes.string,
};
ScrollAware.defaultProps = {
  heightOffset: 0,
  initialScroll: 0,
  onScroll: () => { },
  onScrollContext: () => {},
  className: '',
};


function scrollFadeWithContext(ScrollFadeComponent) {
  class ScrollFadeWithContext extends React.Component {
    render() {
      return (
        <ScrollContext.Consumer>
          {(contextState) => {
            if (!contextState.scrollPosY) return null;

            return (
              <ScrollFadeComponent {...contextState} {...this.props} >
                {this.props.children}
              </ScrollFadeComponent>
            );
          }}
        </ScrollContext.Consumer>
      );
    }
  }
  ScrollFadeWithContext.propTypes = {
    children: PropTypes.any,
  };

  return ScrollFadeWithContext;
}

function mapStateToScrollAwareFadeProps(state, routerProps) {
  const currentColorTheme = colorThemeSelector.getCurrentColorTheme(state);
  const { colorTheme = currentColorTheme } = routerProps;
  return {
    colorTheme,
  };
}

@scrollFadeWithContext
@connect(mapStateToScrollAwareFadeProps)
class ScrollAwareFade extends React.Component {

  getColorRgb() {
    switch (this.props.colorTheme) {
      case 'group':
      case 'backpack':
      case 'profile':
      default:
        return this.props.colorTheme.base_color || this.props.colorTheme.base_rgba;
    }
  }

  render() {
    const { r, g, b } = this.getColorRgb();
    const { height } = this.props;

    return (
      <div
        className="cmp-appheadernavbar_fade"
        style={{
          background: `linear-gradient(to bottom, rgba(${r}, ${g}, ${b}, 1) 60%, rgba(${r}, ${g}, ${b}, 0) 100%)`,
          height,
        }}
      />
    );
  }
}
ScrollAwareFade.propTypes = {
  colorTheme: PropTypes.object,
  height: PropTypes.string,
};

class ScrollAwareHeader extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      style: {},
    };
  }

  componentDidMount() {
    const height =
      this.refContent.offsetHeight || this.refContent.children[0].offsetHeight;

    this.setState({
      style: {
        height: height === 0 ? 10 : height,
        marginTop: height === 0 ? -10 : 0,
      },
    });

    this.props.onGotHeight();
  }

  render() {
    return (
      <div
        className="cmp-scrollaware-header"
        id="cmp-scrollaware-header"
        style={this.state.style}
      >
        <ScrollAwareDropShadow scrollPercent={this.props.scrollPercent} />
        <div
          ref={e => {
            this.refContent = e;
          }}
          className="cmp-scrollaware-header-content "
        >
          {this.props.children}
        </div>
      </div>
    );
  }
}
ScrollAwareHeader.propTypes = {
  children: PropTypes.any,
  scrollPercent: PropTypes.number.isRequired,
  onGotHeight: PropTypes.func,
};

class ScrollAwareScroller extends React.Component {

  constructor(props) {
    super(props);
    this.scrollRef = React.createRef();
  }

  animateScrollTo = (destinationPos, forceDuration) => {
    this.scrollRef.current.animateScrollTo(destinationPos, forceDuration);
  }

  scrollPos = () => {
    const element = this.scrollRef.current.refElement.current;
    return element.scrollTop;
  }

  scrollBy = (destinationPos) => {
    this.scrollRef.current.scrollBy(destinationPos);
  }

  render() {
    return (
      <ScrollContext.Consumer>
        {
          ({ handleScroll, scrollPercent, mobileInnerHeight, setTitle, usesWindowScrolling }) => (
            <ScrollAware {...this.props} usesWindowScrolling={usesWindowScrolling} onScrollContext={handleScroll} scrollPercent={scrollPercent} mobileInnerHeight={mobileInnerHeight} setTitle={setTitle} ref={this.scrollRef} />
          )
        }
      </ScrollContext.Consumer>
    );
  }
}

class ScrollAwareContext extends React.Component {

  static resetScroll() {
    window.scrollTo(0, 0);
  }

  constructor(props) {
    super(props);

    this.handleScroll = this.handleScroll.bind(this);
    this.setTitle = this.setTitle.bind(this);
    this.setStickyOffset = this.setStickyOffset.bind(this);
    this.onHandleScroll = this.onHandleScroll.bind(this);

    const height =
      isMobileApplication() && cordova.plugins.Keyboard.isVisible
        ? window.keyboardHeight
        : window.innerHeight;

    this.state = {
      scrollPosY: 0,
      scrollPercent: 0,
      mobileInnerHeight: height,
      displayTitle: null,
      extendedDisplayTitleHeight: props.extendedDisplayTitleHeight || 0,
      hidePosition: Math.round(props.defaultHidePosition) || 70, // 70px seems to be the standard size of navbar header with the fade
    };
  }

  componentDidMount() {
    if (this.props.usesWindowScrolling) {
      window.addEventListener('scroll', this.onHandleScroll);
    }
  }

  componentWillUnmount() {
    if (this.props.usesWindowScrolling) {
      window.removeEventListener('scroll', this.onHandleScroll);
    }
  }

  onHandleScroll(e) {
    const scrollTop = e.target.scrollingElement ? e.target.scrollingElement.scrollTop : document.body.scrollTop;
    const scrollHeight = e.target.scrollingElement ? e.target.scrollingElement.scrollHeight : document.body.scrollHeight;

    this.setState({
      scrollPosY: scrollTop,
      scrollPercent: scrollTop / scrollHeight,
      mobileInnerHeight: window.keyboardHeight,
    });
  }

  setTitle(displayTitle) {
    this.setState({ displayTitle });
  }

  setStickyOffset(stickyPosition) {
    this.setState({ stickyPosition: Math.round(stickyPosition) });
  }

  handleScroll(scrollTop, scrollHeight) {
    this.setState({
      scrollPosY: scrollTop,
      scrollPercent: scrollTop / scrollHeight,
      mobileInnerHeight: window.keyboardHeight,
    });
  }

  render() {
    const { scrollPosY, scrollPercent, displayTitle, hidePosition, stickyPosition, extendedDisplayTitleHeight } = this.state;

    return (
      <ScrollContext.Provider value={{ scrollPosY, scrollPercent, hidePosition, stickyPosition, extendedDisplayTitleHeight, handleScroll: this.handleScroll, displayTitle, setTitle: this.setTitle, setStickyOffset: this.setStickyOffset, usesWindowScrolling: this.props.usesWindowScrolling }}>
        {this.props.children}
      </ScrollContext.Provider>
    );
  }
}
ScrollAwareContext.propTypes = {
  children: PropTypes.any,
  defaultHidePosition: PropTypes.number,
  usesWindowScrolling: PropTypes.bool,
  extendedDisplayTitleHeight: PropTypes.number,
};
ScrollAwareContext.defaultProps = {
  usesWindowScrolling: true,
};

class ScrollItem extends React.Component {

  componentDidMount() {
    if (this.props.isNav && this.props.setStickyOffset) {
      const height = this.myRef ? (this.myRef.offsetHeight - 0.5) : UI_CONSTANTS.statusBarHeight;
      this.props.setStickyOffset(height);
    }
  }

  componentDidUpdate() {
    const currentPos = this.myRef ? this.myRef.getBoundingClientRect().bottom : 0;
    const displayTitleHeight = this.props.extendedDisplayTitleHeight || 0;

    if (this.props.title) {
      if (currentPos <= this.props.hidePosition) {
        if (!this.props.displayTitle) {
          this.props.setTitle(this.props.title);
        }
      } else if (this.props.displayTitle && currentPos > (this.props.hidePosition + displayTitleHeight)) {
        this.props.setTitle(null);
      }
    }
  }

  render() {
    const { isFixed, isNav, hasDropShadow, isSticky, children, scrollPercent, displayTitle, stickyPosition, isRelative } = this.props;

    return (
      <div ref={(r) => { this.myRef = r; }} className={classnames('cmp-scrollitem', { 'is-sticky': isSticky, 'is-fixed': isFixed, 'is-nav': isNav, 'is-relative': isRelative })} style={{ top: isSticky ? stickyPosition : null }}>
        {hasDropShadow && <ScrollAwareDropShadow scrollPercent={scrollPercent} />}
        {typeof children === 'function' ? children({ displayTitle }) : children}
      </div>
    );
  }
}
ScrollItem.propTypes = {
  isFixed: PropTypes.bool,
  isRelative: PropTypes.bool,
  isNav: PropTypes.bool,
  hasDropShadow: PropTypes.bool,
  isSticky: PropTypes.bool,
  children: PropTypes.any,
  scrollPercent: PropTypes.number,
  displayTitle: PropTypes.any,
  title: PropTypes.any,
  setTitle: PropTypes.func,
  hidePosition: PropTypes.number,
  stickyPosition: PropTypes.number,
  setStickyOffset: PropTypes.func,
  extendedDisplayTitleHeight: PropTypes.number,
};

export function useScrollContext() {
  const contextState = React.useContext(ScrollContext);

  return [contextState];
}

function scrollItemWithContext(Item) {
  class ScrollItemWithContext extends React.Component {
    render() {
      return (
        <ScrollContext.Consumer>
          {(contextState) => {
            return (
              <Item {...contextState} {...this.props} >
                {this.props.children}
              </Item>
            );
          }}
        </ScrollContext.Consumer>
      );
    }
  }
  ScrollItemWithContext.propTypes = {
    children: PropTypes.any,
  };

  return ScrollItemWithContext;
}

const ScrollAwareItem = scrollItemWithContext(ScrollItem);

function mapStateToScrollAwareAppNavStepProps(state) {
  const groupColorTheme = groupSelectors.getActiveGroup(state).theme;
  return {
    groupColorTheme,
  };
}

@connect(mapStateToScrollAwareAppNavStepProps)
class ScrollAwareAppNavStep extends React.Component {
  getBackgroundColor() {
    switch (this.props.colorThemeName) {
      case 'group':
        return 'theme-group base background';
      case 'backpack':
        return 'bkgd-color-backpack-theme-base';
      case 'profile':
      default:
        return 'profile-background-color';
    }
  }

  render() {
    const backgroundColor = this.getBackgroundColor();
    const hasSteps = _.get(this.props, 'stepTotal', 0) > 0;

    if (this.props.colorThemeName === 'group') {
      updateStatusBarColor(this.props.groupColorTheme.base_rgba);
    }

    return (
      <ScrollAwareItem isSticky stickyPosition={0} isNav hasDropShadow={this.props.hasDropShadow}>
        {
          ({ displayTitle }) => (
            <AppNavLeftRightNavigation
              className={`${backgroundColor} ${this.props.className || ''}`}
              {...this.props}
            >
              {
                (hasSteps && this.props.stepTotal > 1) ? (
                  <AppNavStepProgress
                    stepTotal={this.props.stepTotal}
                    currentStep={this.props.currentStep}
                  >
                    {displayTitle && <AppNavHeaderTitle>{displayTitle}</AppNavHeaderTitle>}
                  </AppNavStepProgress>
                ) : (
                  <AppNavHeaderTitle>{displayTitle}</AppNavHeaderTitle>
                )
              }
            </AppNavLeftRightNavigation>
          )
        }
      </ScrollAwareItem>
    );
  }
}
ScrollAwareAppNavStep.propTypes = {
  colorThemeName: PropTypes.string,
  groupColorTheme: PropTypes.any,
  className: PropTypes.string,
  stepTotal: PropTypes.number,
  currentStep: PropTypes.number,
  hasDropShadow: PropTypes.bool,
};

function mapStateToScrollAwareFixedHeaderProps(state, routerProps) {
  const currentColorTheme = colorThemeSelector.getCurrentColorTheme(state);
  const { colorTheme = currentColorTheme } = routerProps;
  return {
    colorTheme,
  };
}
@connect(mapStateToScrollAwareFixedHeaderProps)
class ScrollAwareFixedHeader extends React.Component {
  render() {
    const { colorTheme, colorThemeName, children } = this.props;

    return (
      <ScrollAwareItem isNav isSticky stickyPosition={0}>
        <AppHeader colorThemeName={colorThemeName} colorTheme={colorTheme}>
          {children}
        </AppHeader>
      </ScrollAwareItem>
    );
  }
}
ScrollAwareFixedHeader.propTypes = {
  children: PropTypes.any,
  colorTheme: PropTypes.any,
  colorThemeName: PropTypes.string,
};

export {
  ScrollAwareContext,
  ScrollAwareScroller,
  ScrollAwareItem,
  ScrollAwareFixedHeader,
  ScrollAwareFade,
  ScrollAwareAppNavStep,
};
